import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Col } from "reactstrap";
import { useDispatch } from "react-redux";
import { getRoleForBrokerTables } from "../../../slices/ga/thunk";
import Loading from "../../../Components/Loading";
import EmployerGroupsTable from "../../../Components/Tables/EmployerGroupTable/EmployerGroupTable";
import { postEmployerByBrokerId } from "../../../slices/admin/thunk";
import Swal from "sweetalert2";
import AddRoleModal from "../../../Components/modals/GeneralModals/AddRoleModal";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import DropdownActionsTable from "../../../Components/Dropdown/DropdownActionsTable";
import { downloadEmployersReportByUserId } from "../../../slices/general/thunk";
import { useCurrentUser } from "../../../slices/auth/hooks";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const DashboardEmployersBroker = ({
  isLoaded,
  brokerId,
  openModal,
  setOpenModal,
}) => {
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const isNational = currentUser?.role === DASHBOARD_USER_ROLES.NATIONAL;
  const isAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;

  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const [employers, setEmployers] = useState([]);
  const [totalEmployers, setTotalEmployers] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [currentPage, setCurrentPage] = useState(0);

  const [modalAddEmployerGroups, setModalAddEmployerGroups] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [searchDone, setSearchDone] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        getRoleForBrokerTables({
          id: brokerId || id,
          role: "employers",
          status: "",
          page: currentPage,
          query: searchTerm,
        })
      ).unwrap();
      setEmployers(response.data);
      setTotalEmployers(response.total);
      setPageSize(response.pageSize);
      setTotalPages(response.pages);
      setSearchDone(true);
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoaded) {
      if (debouncedSearchTerm === lastSearchTerm) {
        setSearchDone(true);
      } else {
        setSearchDone(false);
      }
      fetchEmployers();
    }
  }, [brokerId, id, dispatch, debouncedSearchTerm, currentPage]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleCreateEmployer = async (values) => {
    try {
      const response = await dispatch(
        postEmployerByBrokerId({
          id: brokerId || id,
          data: values,
        })
      ).unwrap();

      if (!response.error) {
        Swal.fire({
          title: "Success!",
          text: "Employer group added successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        setLoading(false);
        fetchEmployers();
        setOpenModal(false);
      } else {
        Swal.fire({
          title: "Error!",
          text: response.error,
          icon: "error",
          confirmButtonText: "OK",
        });
        setLoading(false);
        setOpenModal(true);
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text:
          error.message || "An error occurred while adding the employer group",
        icon: "error",
        confirmButtonText: "OK",
      });
      setModalAddEmployerGroups(true);

      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };


  return (
    <React.Fragment>
      <AddRoleModal
        open={openModal}
        setOpen={setOpenModal}
        onCreate={handleCreateEmployer}
        roleToAdd="Employer Group"
      />

      <Container fluid>
        <div className="col-12 d-flex justify-content-between mt-2 mb-3 align-items-center ">
          <div className="col-4 d-flex justify-content-end align-items-center">
            <SearchBar
              inputRef={inputRef}
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Filter by name or email"
            />
            {(isAdmin || isNational) && (
              <DropdownActionsTable
                downloadMethod={downloadEmployersReportByUserId}
                params={{ dashboardUserId: brokerId || id }}
              />
            )}
          </div>
        </div>
        <Col xl={12}>
          <div className="table-container  position-relative">
            {loading && <Loading />}
            <div className="live-preview">
              <div className="table-responsive ">
                <EmployerGroupsTable
                  data={employers}
                  searchDone={searchDone}
                  searchParam={debouncedSearchTerm}
                  paginationProps={{
                    totalItems: totalEmployers,
                    pageSize: pageSize,
                    totalPages: totalPages,
                    currentPage: currentPage,
                    onChangePage: handleChangePage,
                  }}
                />
              </div>
            </div>
          </div>
        </Col>
      </Container>

      <div id="portal-root"></div>
    </React.Fragment>
  );
};

export default DashboardEmployersBroker;
